/**
 * RIG mode (right column): the auto-skin controls — resolution + solver stats, per-joint
 * boundary correction, the test-pose gallery with leak detection, and region reassignment.
 * (Vertex painting is gone: corrections are solver parameters now, not per-vertex pixels.)
 *
 * Two rendering rules, both forced by `createPanel` rebuilding the whole subtree:
 *  - the panel does NOT subscribe to `rig.skinParams`. Every slider drag writes that slice, and
 *    a rebuild mid-drag would rip the slider out of the DOM. Params are read on demand instead.
 *  - `'skin-changed'`, `leakSummary`, `previewPose` and `skinTool` all change during a re-solve
 *    (i.e. mid-drag), so they are handled by `patchLive()` — text, `.active` and `disabled`
 *    rewritten in place — never by a rebuild.
 * The only structural rebuild triggers are `selectedJoint` / `skinGenerated` / `skinBusy`: they
 * change what controls exist, and they cannot change during a drag.
 */

import type { CanonicalBoneId, SkinParams, SkinResolution } from '../../shared';
import type { TestPoseId } from '../../rig/autoskin/types';
import type { EditorContext, SkinGenerationStats } from '../context';
import type { Controllers } from '../controllers/types';
import { button, el, icon, iconButton, select, switchField, type Child } from './dom';
import {
  createPanel,
  field,
  note,
  readonlyField,
  runAsync,
  section,
  type PanelHandle,
} from './panel';

const RESOLUTIONS: readonly SkinResolution[] = [64, 96, 128, 192];

/**
 * Panels must not import `src/rig/**` at runtime, so the two `jointParamsOf` lookups the UI
 * needs are inlined here (the solver still owns the real clamping).
 */
function jointOffset(params: SkinParams, bone: CanonicalBoneId): number {
  return params.joints[bone]?.offset ?? 0;
}

function jointBlend(params: SkinParams, bone: CanonicalBoneId): number {
  return params.joints[bone]?.blend ?? params.blend;
}

function reassignHint(target: string): string {
  return `ON: click the mesh — the region under the cursor is assigned to the selected joint (${target})`;
}

/**
 * What the last solve produced, in one line. The four remaining numbers are debugging data and
 * live in the `<details>` below it: five lines of solver dump pushed every control in this panel
 * further down the column than the screen is tall (audit B).
 */
function statsLine(stats: SkinGenerationStats | null): string {
  if (!stats) return '';
  return [
    `res ${stats.resolution}`,
    stats.closed ? 'closed shell' : 'open shell',
    `${stats.vertexCount} verts`,
  ].join(' · ');
}

/** The rest of the solver report — timings first, then whatever went wrong. */
function statsDetail(stats: SkinGenerationStats | null): string {
  if (!stats) return '';
  const parts = [
    `voxel ${Math.round(stats.buildMs)} ms`,
    `geodesic ${Math.round(stats.geodesicMs)} ms`,
    `solve ${Math.round(stats.solveMs)} ms`,
    `${stats.uniqueCount} unique`,
  ];
  if (stats.pulledInside.length > 0) parts.push(`pulled inside: ${stats.pulledInside.join(', ')}`);
  if (stats.unreachable > 0) parts.push(`unreachable ${stats.unreachable}`);
  return parts.join(' · ');
}

/**
 * One dispatch per animation frame while a slider is dragged. Trailing edge: the newest value
 * wins, and `change` cancels the pending frame and commits the final value itself.
 */
interface FrameThrottle {
  push(fn: () => void): void;
  cancel(): void;
}

function createFrameThrottle(): FrameThrottle {
  let pending: (() => void) | null = null;
  let scheduled = false;

  const run = (): void => {
    scheduled = false;
    const fn = pending;
    pending = null;
    fn?.();
  };

  return {
    push(fn) {
      pending = fn;
      if (scheduled) return;
      scheduled = true;
      // jsdom/node without rAF (and the tests) fall back to a macrotask.
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
      else setTimeout(run, 0);
    },
    cancel() {
      pending = null;
    },
  };
}

interface SliderSpec {
  value: number;
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  role: string;
  /** Live readout while dragging (never dispatches). */
  onLive: (value: number) => void;
  /**
   * Commit — throttled on `input` (`final` false), immediate on `change` (`final` true). The
   * final commit is the one that persists: mid-drag re-solves stay debounced.
   */
  onCommit: (value: number, final: boolean) => void;
}

function slider(spec: SliderSpec, throttle: FrameThrottle): HTMLInputElement {
  const node = el('input', {
    type: 'range',
    min: spec.min,
    max: spec.max,
    step: spec.step,
    class: 'slider',
    'data-role': spec.role,
    disabled: spec.disabled,
  });
  node.value = String(spec.value);
  node.addEventListener('input', () => {
    const value = Number(node.value);
    spec.onLive(value);
    throttle.push(() => spec.onCommit(value, false));
  });
  node.addEventListener('change', () => {
    const value = Number(node.value);
    spec.onLive(value);
    throttle.cancel();
    spec.onCommit(value, true);
  });
  return node;
}

/** Where the value sits in the slider's travel, as the `--p` fill width of a `.field.fill`. */
function fillPercent(value: number, min: number, max: number): string {
  const t = max > min ? (value - min) / (max - min) : 0;
  return `${Math.round(Math.min(1, Math.max(0, t)) * 100)}%`;
}

/**
 * The signature slot with a slider inside it (`design/ui-design.md` §4, `.slot.fill`): the blue
 * fill IS the slider and the range input is a transparent overlay on top of it. The control stays
 * a real `<input type="range">`, so the keyboard, the frame throttle and the tests are untouched.
 */
function sliderSlot(
  label: string,
  spec: Omit<SliderSpec, 'onLive'>,
  format: (value: number) => string,
  throttle: FrameThrottle,
): HTMLElement {
  const readout = el('span', { class: 'readout', text: format(spec.value) });
  const slot = el('div', { class: spec.disabled ? 'field fill off' : 'field fill' }, [
    el('span', { class: 'flabel', text: label }),
    readout,
  ]);
  slot.style.setProperty('--p', fillPercent(spec.value, spec.min, spec.max));
  slot.appendChild(
    slider(
      {
        ...spec,
        onLive: (value) => {
          readout.textContent = format(value);
          slot.style.setProperty('--p', fillPercent(value, spec.min, spec.max));
        },
      },
      throttle,
    ),
  );
  return slot;
}

/** Metres, to millimetre resolution — the unit every skin parameter is expressed in. */
function metres(value: number): string {
  return `${value.toFixed(3)} m`;
}

export function mountSkinPanel(
  container: HTMLElement,
  ctx: EditorContext,
  _controllers: Controllers,
): PanelHandle {
  const { store } = ctx;
  const tools = ctx.rigTools;
  const throttle = createFrameThrottle();

  /*
   * Patch targets. Everything that can change *while a slider is being dragged* is rewritten in
   * place instead of rebuilding the subtree: the controller dispatches a fresh `leakSummary`
   * after a re-solve, and a rebuild would rip the slider out of the DOM mid-drag. Only
   * `selectedJoint` / `skinGenerated` / `skinBusy` rebuild (see `slices` at the bottom).
   */
  let statusNode: HTMLElement | null = null;
  let detailNode: HTMLElement | null = null;
  let moreNode: HTMLDetailsElement | null = null;
  let leakNode: HTMLElement | null = null;
  let overridesNode: HTMLElement | null = null;
  let regionNoteNode: HTMLElement | null = null;
  /** The `<input type="checkbox">` inside the reassign switch — `patchLive` sets its `checked`. */
  let toggleNode: HTMLInputElement | null = null;
  let checkNode: HTMLButtonElement | null = null;
  let fixNode: HTMLButtonElement | null = null;
  /** key: `'rest'` or a `TestPoseId`. */
  const poseNodes = new Map<string, HTMLButtonElement>();

  /**
   * Persist a finished edit. The public `resolveFromCache()` re-solves from the cached distance
   * fields and ends with an immediate save, so it is called only on the *final* commit of an
   * interaction (drag end, checkbox, reset) — never on the throttled mid-drag dispatches.
   */
  function persist(): void {
    runAsync(tools.resolveFromCache());
  }

  function leakLine(): string {
    const summary = store.getState().rig.leakSummary;
    if (!summary) return '';
    return `${summary.count} leaked vertices (max ${(summary.maxDisplacement * 1000).toFixed(1)} mm)`;
  }

  /** The bone the viewport would reassign to (it falls back to `selection.bone`). */
  function regionTarget(): string {
    const state = store.getState();
    return state.rig.selectedJoint ?? state.selection.bone ?? 'none';
  }

  /**
   * Rewrite every derived text / flag from current state, without touching the structure. Safe to
   * call from a store subscription (DOM only, no dispatch).
   */
  function patchLive(): void {
    const state = store.getState();
    const rig = state.rig;
    const locked = rig.skinBusy || !rig.skinGenerated;

    const stats = tools.skinStats();
    if (statusNode) statusNode.textContent = statsLine(stats);
    if (detailNode) detailNode.textContent = statsDetail(stats);
    // Nothing to disclose before the first solve — an empty toggle is worse than no toggle.
    if (moreNode) moreNode.hidden = stats === null;
    if (leakNode) leakNode.textContent = leakLine();
    if (overridesNode) overridesNode.textContent = String(rig.skinParams.regionOverrides.length);
    if (regionNoteNode) regionNoteNode.textContent = reassignHint(regionTarget());
    // A switch carries its state in `checked`, not in its label — patch the flag, not the text.
    if (toggleNode) toggleNode.checked = rig.skinTool === 'reassign';
    for (const [key, node] of poseNodes) {
      node.classList.toggle('active', key === (rig.previewPose ?? 'rest'));
    }
    if (checkNode) checkNode.disabled = locked || !rig.previewPose;
    if (fixNode) fixNode.disabled = locked || !rig.previewPose;
  }

  function autoSkinSection(busy: boolean, generated: boolean): HTMLElement {
    const params = store.getState().rig.skinParams;
    const stats = tools.skinStats();
    statusNode = el('p', { class: 'note', 'data-role': 'skin-status', text: statsLine(stats) });
    detailNode = el('p', { class: 'note dim', text: statsDetail(stats) });
    moreNode = el('details', { class: 'more' }, [el('summary', { text: 'details' }), detailNode]);
    moreNode.hidden = stats === null;
    return section(
      'Skin weights',
      [
        field(
          'Resolution',
          select(
            RESOLUTIONS.map((value) => ({ value: String(value), label: String(value) })),
            String(params.resolution),
            (value) => store.actions.setSkinResolution(Number(value) as SkinResolution),
            { 'data-role': 'skin-resolution', disabled: busy },
          ),
        ),
        note('applies on next Generate', 'note dim'),
        // Legend for the viewport heat ramp: the weight scale is a reading aid, not an accent.
        el('div', { class: 'legend' }, [
          el('span', { class: 'dim', text: 'weight' }),
          el('span', { class: 'ramp' }),
          el('span', { class: 'dim', text: '1.0' }),
        ]),
        statusNode,
        moreNode,
        busy ? note('Computing…', 'note busy') : null,
        generated ? null : note('Generate skin weights first (RIG panel, left).'),
      ],
      { icon: 'sliders', key: 'Auto-skin' },
    );
  }

  function correctionSection(locked: boolean, joint: CanonicalBoneId | null): HTMLElement {
    const params = store.getState().rig.skinParams;
    const children: Child[] = [];

    if (joint) {
      children.push(
        field('Joint', el('span', { 'data-role': 'skin-joint', text: joint })),
        sliderSlot(
          'Boundary offset',
          {
            value: jointOffset(params, joint),
            min: -0.1,
            max: 0.1,
            step: 0.005,
            disabled: locked,
            role: 'offset',
            onCommit: (value, final) => {
              store.actions.setSkinJointParams(joint, { offset: value });
              if (final) persist();
            },
          },
          metres,
          throttle,
        ),
        sliderSlot(
          'Blend width',
          {
            value: jointBlend(params, joint),
            min: 0.005,
            max: 0.1,
            step: 0.005,
            disabled: locked,
            role: 'joint-blend',
            onCommit: (value, final) => {
              store.actions.setSkinJointParams(joint, { blend: value });
              if (final) persist();
            },
          },
          metres,
          throttle,
        ),
      );
    } else {
      children.push(note('Select a joint in the hierarchy to correct its boundary.'));
    }

    children.push(
      switchField(
        'Symmetry L/R',
        params.symmetry,
        (checked) => {
          store.actions.setSkinSymmetry(checked);
          persist();
        },
        { 'data-role': 'skin-symmetry', disabled: locked },
      ),
      sliderSlot(
        'Blend width, all joints',
        {
          value: params.blend,
          min: 0.005,
          max: 0.1,
          step: 0.005,
          disabled: locked,
          role: 'global-blend',
          onCommit: (value, final) => {
            store.actions.setSkinBlend(value);
            if (final) persist();
          },
        },
        metres,
        throttle,
      ),
      el('div', { class: 'buttons' }, [
        button(
          [icon('reset'), 'Reset joint'],
          () => {
            if (!joint) return;
            store.actions.resetSkinJoint(joint);
            persist();
            instance.schedule();
          },
          { 'data-action': 'reset-joint', disabled: locked || !joint },
        ),
        button(
          [icon('reset'), 'Reset all'],
          () => {
            store.actions.resetSkinParams();
            persist();
            instance.schedule();
          },
          { 'data-action': 'reset-all', disabled: locked },
        ),
      ]),
    );

    return section('Per-joint correction', children, {
      meta: joint ?? undefined,
      icon: 'target',
      key: 'Joint correction',
    });
  }

  function poseSection(locked: boolean): HTMLElement {
    const pose = store.getState().rig.previewPose;
    poseNodes.clear();

    const poseButton = (key: string, label: string, id: TestPoseId | null): HTMLButtonElement => {
      const node = button(label, () => store.actions.setPreviewPose(id), {
        'data-pose': key,
        class: pose === id ? 'pill active' : 'pill',
        disabled: locked,
      });
      poseNodes.set(key, node);
      return node;
    };

    const buttons: Child[] = [
      poseButton('rest', 'Rest', null),
      ...tools.testPoses().map((entry) => poseButton(entry.id, entry.label, entry.id)),
    ];

    leakNode = el('p', { class: 'note', 'data-role': 'leak-summary', text: leakLine() });

    // The pose is read at click time: `previewPose` no longer rebuilds this subtree.
    checkNode = button(
      [icon('check'), 'Check leaks'],
      () => {
        const current = store.getState().rig.previewPose;
        if (!current) return;
        runAsync(tools.runLeakCheck(current));
      },
      { 'data-action': 'check-leaks', disabled: locked || !pose },
    );
    fixNode = button(
      [icon('zap'), 'Fix leaks'],
      () => {
        const current = store.getState().rig.previewPose;
        if (!current) return;
        // Fixing rewrites the joint offsets — re-render so the sliders show the new values.
        runAsync(tools.fixLeaks(current), () => instance.schedule());
      },
      { 'data-action': 'fix-leaks', disabled: locked || !pose },
    );

    return section(
      'Test poses',
      [
        el('div', { class: 'buttons pills' }, buttons),
        el('div', { class: 'buttons' }, [
          checkNode,
          fixNode,
          // Icon-only: three labelled buttons wrapped onto two lines in a 340px column, and
          // "revert" is the one of the three nobody needs to read to recognise.
          iconButton(
            'undo',
            'Revert fix',
            // Reverting restores the previous parameters — same staleness problem as "Fix leaks".
            () => runAsync(tools.revertLastFix(), () => instance.schedule()),
            { 'data-action': 'revert-fix', disabled: locked },
          ),
        ]),
        leakNode,
      ],
      { icon: 'eye' },
    );
  }

  function regionSection(locked: boolean): HTMLElement {
    const rig = store.getState().rig;
    overridesNode = el('span', {
      'data-role': 'override-count',
      text: String(rig.skinParams.regionOverrides.length),
    });
    regionNoteNode = el('p', {
      class: 'note',
      'data-role': 'reassign-hint',
      text: reassignHint(regionTarget()),
    });
    /*
     * A switch, not a button whose label says ON/OFF: the tool is a mode the user leaves armed
     * while clicking the mesh, and a text-only button never showed that it was still armed. The
     * `data-action` lands on the checkbox (that is what `switchField` does with attrs), so the
     * click target is unchanged; `patchLive` keeps `checked` in step with the store.
     */
    const toggle = switchField(
      'Reassign region on click',
      rig.skinTool === 'reassign',
      (on) => store.actions.setSkinTool(on ? 'reassign' : 'none'),
      { 'data-action': 'toggle-reassign', disabled: locked },
    );
    toggleNode = toggle.querySelector<HTMLInputElement>('input');

    return section(
      'Region',
      [
        toggle,
        regionNoteNode,
        field('Overrides', overridesNode),
        button([icon('trash'), 'Clear overrides'], () => runAsync(tools.clearRegionOverrides()), {
          'data-action': 'clear-overrides',
          disabled: locked,
        }),
      ],
      { icon: 'map' },
    );
  }

  function render(root: HTMLElement): void {
    const rig = store.getState().rig;
    const busy = rig.skinBusy;
    const locked = busy || !rig.skinGenerated;
    const joint = rig.selectedJoint;

    root.appendChild(autoSkinSection(busy, rig.skinGenerated));
    root.appendChild(correctionSection(locked, joint));
    root.appendChild(poseSection(locked));
    root.appendChild(regionSection(locked));

    const data = tools.skinData();
    if (data) {
      root.appendChild(
        section(
          'Mesh',
          [
            readonlyField('Name', data.meshName),
            readonlyField('Vertices', String(data.vertexCount)),
          ],
          { icon: 'box' },
        ),
      );
    }
  }

  /*
   * Structure-only rebuild triggers. `previewPose`, `leakSummary` and `skinTool` are NOT here:
   * the controller dispatches them during a re-solve (i.e. mid-drag), and they change nothing
   * structural — `patchLive()` handles their text / active class / disabled state instead.
   * `skinParams` is not subscribed at all (params are read on demand).
   */
  const instance = createPanel(container, ctx, render, {
    className: 'skin',
    slices: [(s) => s.rig.selectedJoint, (s) => s.rig.skinGenerated, (s) => s.rig.skinBusy],
    // NOT `events: ['skin-changed']` — that would rebuild (and destroy) the sliders mid-drag.
  });

  instance.own(ctx.events.on('skin-changed', patchLive));
  instance.own(store.select((s) => s.rig.leakSummary, patchLive));
  instance.own(store.select((s) => s.rig.previewPose, patchLive));
  instance.own(store.select((s) => s.rig.skinTool, patchLive));
  // (the override count rides on 'skin-changed', which every reassignment emits)
  instance.own(store.select((s) => s.selection.bone, patchLive));
  instance.own(() => throttle.cancel());

  return instance;
}
